import { fileSize } from './supportTypes'

export function AttachmentPicker({
  files,
  onChange,
  error,
  onError,
}: {
  files: File[]
  onChange: (files: File[]) => void
  error: string | null
  onError: (message: string | null) => void
}) {
  function addFiles(selected: FileList | null) {
    const next = [...files, ...Array.from(selected ?? [])]
    if (next.length > 5) {
      onError('You can attach up to five files.')
      return
    }
    if (next.some(file => file.size > 5_000_000)) {
      onError('Please choose files smaller than 5 MB each.')
      return
    }
    if (next.reduce((total, file) => total + file.size, 0) > 10_000_000) {
      onError('Please keep your attachments under 10 MB in total.')
      return
    }
    onChange(next)
    onError(null)
  }

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-600">
        Attachments (optional)
        <input
          type="file"
          multiple
          aria-describedby="support-file-limits"
          className="mt-2 block w-full text-sm file:mr-3 file:rounded-md file:border-0 file:bg-gray-100 file:px-3 file:py-2 file:text-gray-700"
          onChange={event => {
            addFiles(event.target.files)
            event.target.value = ''
          }}
        />
      </label>
      <p id="support-file-limits" className="text-xs text-gray-500">
        Up to five files, 5 MB each and 10 MB in total. Files are checked before
        they can be downloaded.
      </p>
      {files.length > 0 && (
        <ul className="space-y-2">
          {files.map((file, index) => (
            <li
              key={`${file.name}-${index}`}
              className="flex items-center justify-between gap-3 rounded-lg bg-gray-50 px-3 py-2 text-sm"
            >
              <span className="min-w-0 break-all">
                {file.name}{' '}
                <span className="text-gray-500">({fileSize(file.size)})</span>
              </span>
              <button
                type="button"
                className="shrink-0 text-vi-red underline"
                aria-label={`Remove ${file.name}`}
                onClick={() => {
                  onChange(files.filter((_, position) => position !== index))
                  onError(null)
                }}
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}
      {error && (
        <p role="alert" className="text-sm text-red-700">
          {error}
        </p>
      )}
    </div>
  )
}

export async function readAttachments(files: File[]) {
  return Promise.all(
    files.map(
      file =>
        new Promise<{ name: string; contentType: string; base64: string }>(
          (resolve, reject) => {
            const reader = new FileReader()
            reader.onerror = () =>
              reject(
                new Error(
                  `We couldn’t read ${file.name}. Please select the file again.`
                )
              )
            reader.onload = () =>
              resolve({
                name: file.name,
                contentType: file.type || 'application/octet-stream',
                base64: String(reader.result).split(',')[1],
              })
            reader.readAsDataURL(file)
          }
        )
    )
  )
}
